import { useMemo } from "react";

import { SettingsPillChoice } from "./SettingsPillChoice";
import type { UiTheme } from "../theme";

// PG-2 — the shared "week starts on" picker used by mobile Settings AND the
// web-rnw console Preferences, so the pill UI + the localized weekday labels
// live once (RNW-shared-first). Data orchestration (get-merge-PUT of
// Profile.weekStartDay) stays in each mount — this is display + signal only.
//
// Weekday labels come from Intl, not 35 hand-written i18n strings: the runtime
// gives locale-correct short names (Mon / Lun / Mo / Пн) for free and follows
// the app locale. Values are the JS getDay() index (0=Sun..6=Sat); the row is
// ordered Monday-first (the default + the EU norm), Sunday last.

const WEEK_START_ORDER = [1, 2, 3, 4, 5, 6, 0];

// A fixed reference week whose Jan 1 2023 is a Sunday, so `1 + day` lands on the
// weekday with getDay() === day. Formatted in UTC to avoid an off-by-one from
// the runner's zone.
const weekdayLabel = (day: number, locale: string): string =>
  new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" }).format(
    new Date(Date.UTC(2023, 0, 1 + day)),
  );

export function WeekStartChoice({
  theme,
  value,
  onPick,
  locale,
  label,
  help,
  saving = false,
  error = null,
}: {
  theme: UiTheme;
  value: number;
  onPick: (day: number) => void;
  locale: string;
  label: string;
  help?: string;
  saving?: boolean;
  error?: string | null;
}) {
  const options = useMemo(
    () =>
      WEEK_START_ORDER.map((d) => ({
        value: String(d),
        label: weekdayLabel(d, locale),
      })),
    [locale],
  );

  return (
    <SettingsPillChoice
      theme={theme}
      label={label}
      help={help}
      options={options}
      value={String(value)}
      onPick={(v) => onPick(Number(v))}
      saving={saving}
      error={error}
      compact
    />
  );
}
